import React, { useState, useEffect } from 'react';
import { PieChart, Package, Users, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

const CategoryAnalysis = () => {
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/trm/stats/by-category`);
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      setCategoryData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading category analysis...</div>;
  if (error) return <div style={{ padding: '40px', color: 'var(--cares-orange)' }}>Error: {error}</div>;
  if (!categoryData) return null;

  // Calculate totals
  const totalProducts = categoryData.reduce((sum, cat) => sum + cat.product_count, 0);
  const totalManufacturers = categoryData.reduce((sum, cat) => sum + cat.manufacturer_count, 0);
  const totalVersions = categoryData.reduce((sum, cat) => sum + cat.version_count, 0);

  // Color palette for categories
  const colors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16'
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--cares-text-2)', marginBottom: '8px' }}>
          Category Analysis
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>
          Analyze technology portfolio distribution across categories
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '32px' }}>
        <div style={{ background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)', padding: '24px', borderRadius: '12px', color: 'var(--cares-text-max)' }}>
          <PieChart size={32} style={{ marginBottom: '12px' }} />
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>{categoryData.length}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Categories</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, var(--cares-magenta) 0%, var(--cares-magenta) 100%)', padding: '24px', borderRadius: '12px', color: 'var(--cares-text-max)' }}>
          <Package size={32} style={{ marginBottom: '12px' }} />
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>{totalProducts}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Products</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, var(--cares-blue) 0%, var(--cares-teal) 100%)', padding: '24px', borderRadius: '12px', color: 'var(--cares-text-max)' }}>
          <Users size={32} style={{ marginBottom: '12px' }} />
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>{totalManufacturers}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Manufacturers</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, var(--cares-magenta) 0%, color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card)) 100%)', padding: '24px', borderRadius: '12px', color: 'var(--cares-text-max)' }}>
          <TrendingUp size={32} style={{ marginBottom: '12px' }} />
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>{totalVersions}</div>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Versions</div>
        </div>
      </div>

      {/* Category Distribution Chart */}
      <div style={{ background: 'var(--cares-surface-card)', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)', marginBottom: '24px' }}>
          Product Distribution by Category
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {categoryData.map((cat, idx) => {
            const percentage = ((cat.product_count / totalProducts) * 100).toFixed(1);
            const color = colors[idx % colors.length];
            
            return (
              <div key={cat.category}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-2)' }}>{cat.category}</span>
                  <span style={{ fontSize: '14px', color: 'var(--cares-text-label)' }}>{cat.product_count} products ({percentage}%)</span>
                </div>
                <div style={{ height: '8px', background: 'var(--cares-surface-board)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${percentage}%`,
                    height: '100%',
                    background: color,
                    transition: 'width 0.5s ease'
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Category Details Table */}
      <div style={{ background: 'var(--cares-surface-card)', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-2)', marginBottom: '20px' }}>
          Category Details
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--cares-line-card)' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Category</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Products</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Manufacturers</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Versions</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--cares-text-label)', textTransform: 'uppercase' }}>Avg Versions/Product</th>
              </tr>
            </thead>
            <tbody>
              {categoryData.map((cat, idx) => {
                const avgVersions = (cat.version_count / cat.product_count).toFixed(1);
                const color = colors[idx % colors.length];
                
                return (
                  <tr key={cat.category} style={{ borderBottom: '1px solid var(--cares-line)', ':hover': { background: 'var(--cares-surface-board)' } }}>
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '4px', height: '32px', background: color, borderRadius: '2px' }} />
                        <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-2)' }}>{cat.category}</span>
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right', fontSize: '14px', color: 'var(--cares-text-2)', fontWeight: '600' }}>
                      {cat.product_count}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right', fontSize: '14px', color: 'var(--cares-text-label)' }}>
                      {cat.manufacturer_count}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right', fontSize: '14px', color: 'var(--cares-text-label)' }}>
                      {cat.version_count}
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: color }}>
                      {avgVersions}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CategoryAnalysis;
